/**
 * Signed-out ("public") routes. The app has no react-router: App.js reads
 * `window.location.pathname` once and keeps the current public view in state,
 * so these helpers map a path to a view and back, and keep the address bar in
 * step with pushState so the Back button works between public pages.
 *
 * Only the paths below are owned by the public site. Any other path a
 * signed-out visitor opens (an emailed deep link like
 * /documents/12/signed or /admin/ownership-review?case=4) shows the sign-in
 * view and keeps its URL, so the path-based route can render after sign-in.
 */

/** Public view → canonical path. */
export const PUBLIC_PATHS = {
  landing: '/',
  pricing: '/pricing',
  'list-home': '/sell',
  login: '/login',
  register: '/signup',
};

const HOME_PATH = /^\/homes\/(\d+)\/?$/;

/**
 * Shareable URL path of a public listing page.
 *
 * @param {number|string} houseId
 * @returns {string} e.g. "/homes/42"
 */
export function homePath(houseId) {
  return `/homes/${houseId}`;
}

/**
 * Absolute shareable URL of a public listing page on this origin.
 *
 * @param {number|string} houseId
 * @returns {string}
 */
export function homeShareUrl(houseId) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${homePath(houseId)}`;
}

/**
 * The public listing id in a path, or null.
 *
 * @param {string} pathname
 * @returns {number|null}
 */
export function homeIdForPath(pathname) {
  const match = HOME_PATH.exec(String(pathname || ''));
  return match ? Number(match[1]) : null;
}

/**
 * Which signed-out view a path opens.
 *
 * @param {string} pathname - e.g. window.location.pathname.
 * @returns {{view: string, homeId: number|null}} view is one of the
 *   PUBLIC_PATHS keys or 'home'. Unknown paths open 'login' (see module note).
 */
export function publicViewForPath(pathname) {
  const homeId = homeIdForPath(pathname);
  if (homeId) return { view: 'home', homeId };
  const normalized = String(pathname || '').replace(/\/+$/, '') || '/';
  const view = Object.keys(PUBLIC_PATHS).find((key) => PUBLIC_PATHS[key] === normalized);
  return { view: view || 'login', homeId: null };
}

/**
 * True when the path belongs to the public site (so moving between public
 * views may rewrite it). Deep links into the app are never rewritten.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export function isPublicSitePath(pathname) {
  const normalized = String(pathname || '').replace(/\/+$/, '') || '/';
  return Object.values(PUBLIC_PATHS).includes(normalized) || homeIdForPath(pathname) !== null;
}

// ── What the visitor wanted to do before signing in ────────────────────────

const INTENT_KEY = 'cutthe6.publicIntent';

// The sign-in detour is a few minutes at most; an older intent is stale.
const INTENT_MAX_AGE_MS = 60 * 60 * 1000;

/** Actions on a public listing that need an account. */
export const PUBLIC_INTENT_ACTIONS = ['view', 'tour', 'offer'];

/**
 * Remember that a signed-out visitor asked to tour or make an offer on a
 * listing, so the app can open it once they have signed in or signed up.
 * sessionStorage: it only has to survive the in-page sign-in, and must not
 * follow the next person on a shared computer.
 *
 * @param {{homeId: number, action: 'view'|'tour'|'offer'}} intent
 */
export function savePublicIntent({ homeId, action }) {
  if (!homeId || !PUBLIC_INTENT_ACTIONS.includes(action)) return;
  try {
    window.sessionStorage.setItem(
      INTENT_KEY,
      JSON.stringify({ homeId: Number(homeId), action, savedAt: Date.now() }),
    );
  } catch {
    // Storage blocked: after sign-in the visitor lands on their default tab.
  }
}

/**
 * Read and clear the saved intent (one-shot).
 *
 * @returns {{homeId: number, action: string}|null}
 */
export function takePublicIntent() {
  let raw = null;
  try {
    raw = window.sessionStorage.getItem(INTENT_KEY);
    window.sessionStorage.removeItem(INTENT_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const fresh = typeof parsed?.savedAt === 'number' && Date.now() - parsed.savedAt <= INTENT_MAX_AGE_MS;
    if (!fresh || !Number.isInteger(parsed.homeId) || parsed.homeId <= 0) return null;
    if (!PUBLIC_INTENT_ACTIONS.includes(parsed.action)) return null;
    return { homeId: parsed.homeId, action: parsed.action };
  } catch {
    return null;
  }
}
